import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IComptable, defaultValue } from 'app/shared/model/comptable.model';

const initialState: EntityState<IComptable> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IComptable>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/users/comptables';

// Actions

export const getComptables = createAsyncThunk(
  'comptable/fetch_comptable_list',
  async (id: string | number) => {
    return axios.get<IComptable[]>(`${apiUrl}?ecoleId=${id}`);
  },
  { serializeError: serializeAxiosError }
);

export const getComptable = createAsyncThunk(
  'comptable/fetch_comptable',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IComptable>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createComptable = createAsyncThunk(
  'comptable/create_comptable',
  async (entity: IComptable, thunkAPI) => {
    const result = await axios.post<IComptable>(`${apiUrl}/${entity.ecoleId}`, cleanEntity(entity));
    thunkAPI.dispatch(getComptables(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateComptable = createAsyncThunk(
  'comptable/update_comptable',
  async (entity: IComptable, thunkAPI) => {
    const result = await axios.put<IComptable>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getComptables(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteComptable = createAsyncThunk(
  'comptable/delete_comptable',
  async (entity: IComptable, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IComptable>(requestUrl);
    thunkAPI.dispatch(getComptables(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export type ComptableState = Readonly<typeof initialState>;
// slice

export const ComptableSlice = createSlice({
  name: 'comptable',
  initialState: initialState as ComptableState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getComptable.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteComptable.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getComptables), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createComptable, updateComptable), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getComptables, getComptable), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createComptable, updateComptable, deleteComptable), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ComptableSlice.actions;

// Reducer
export default ComptableSlice.reducer;
